import type { Doc } from '../_generated/dataModel'
import type { QueryCtx } from '../_generated/server'
import { addDaysKey } from './goalFreeze'
import { roundAmount } from './habitMeasure'

export const HOUR_MS = 3_600_000

export const fastHours = (startedAt: number, endedAt: number) =>
  roundAmount(Math.max(0, endedAt - startedAt) / HOUR_MS)

// A fast counts on the day it ends, so the days it spans before that are rest
// days (never missed). Capped at target + 24 h so a forgotten timer doesn't
// excuse days forever.
export function fastRestDays(
  fasts: ReadonlyArray<{ startedAt: number; endedAt: number }>,
  targetHours: number,
  dayOf: (ms: number) => string,
): string[] {
  const out = new Set<string>()
  for (const f of fasts) {
    const end = Math.min(f.endedAt, f.startedAt + (targetHours + 24) * HOUR_MS)
    const endKey = dayOf(end)
    let k = dayOf(f.startedAt)
    for (let i = 0; i < 16 && k < endKey; i++, k = addDaysKey(k, 1)) out.add(k)
  }
  return [...out].sort()
}

// Every day in [from, to]: a one-off habit is never due, so none of them can be missed.
export function dayKeysBetween(from: string, to: string, max = 400): string[] {
  const out: string[] = []
  for (let k = from; k <= to && out.length < max; k = addDaysKey(k, 1)) out.push(k)
  return out
}

// Rest days of a fasting habit's fasts (logged and running) from `fromKey` on.
export async function habitFastRestDays(
  ctx: QueryCtx,
  habit: Doc<'nodes'>,
  dayOf: (ms: number) => string,
  fromKey: string,
  now: number,
): Promise<string[]> {
  if (!habit.measure?.fast) return []
  const logs = await ctx.db
    .query('habitLogs')
    .withIndex('by_habit_and_date', (q) => q.eq('habitId', habit._id).gte('date', fromKey))
    .take(1000)
  const fasts = logs.flatMap((l) =>
    l.startedAt !== undefined ? [{ startedAt: l.startedAt, endedAt: l.createdAt }] : [],
  )
  if (habit.fast) fasts.push({ startedAt: habit.fast.startedAt, endedAt: now })
  return fastRestDays(fasts, habit.measure.target, dayOf)
}
